/* Notification Center — "a rule can only be turned ON once it is complete" (owner request 2026-10-24).
 * A draft (New Rule wizard → Save as draft) is saved OFF at any stage; turning it on — in Rule Detail or with the
 * list's Enabled toggle — needs everything an alert needs to go out. The SERVER is authoritative (the rule save
 * refuses an incomplete enabled rule with the same wording, NotificationRulesWriter THROW 50433); this mirror
 * only says it first, without a round trip. Categories are decided by CODE: RC-01 needs trigger forms, RC-02's
 * targets are its hold-time tests, every other category's are Permit Library parameters. */
import { HOLD_TIME_CATEGORY_CODE, RESULT_EXCEEDANCE_CATEGORY_CODE } from './categoryCodes';
import type { NcRule } from './data';

/** What decides completeness, counted from whatever the caller is showing (saved rule or Rule Detail draft). */
export interface RuleCompletenessInput {
  /** The rule's Category Code label, e.g. "RC-01". */
  categoryCode: string | undefined;
  triggerCount: number;
  targetCount: number;
  /** Live (not deleted) Recipient Groups. */
  recipientCount: number;
  inApp: boolean;
  email: boolean;
}

/** The missing pieces, in the order the server lists them; empty = complete. */
export function ruleMissing(r: RuleCompletenessInput): string[] {
  const out: string[] = [];
  if (r.categoryCode === RESULT_EXCEEDANCE_CATEGORY_CODE && r.triggerCount === 0) out.push('trigger forms');
  if (r.targetCount === 0) out.push('targets');
  if (r.recipientCount === 0) out.push('recipients');
  if (!r.inApp && !r.email) out.push('a delivery method (In-App or Email)');
  return out;
}

/** The refusal shown when an incomplete rule is turned on — the server's wording. */
export function incompleteMessage(missing: string[]): string {
  return `The rule can only be turned on once it is complete. Missing: ${missing.join(', ')}. Finish it, or turn it off to save it as a draft.`;
}

/** Completeness of a SAVED rule (the list's Enabled toggle). */
export function savedRuleMissing(rule: NcRule, categoryCode: string | undefined): string[] {
  const isHoldTime = categoryCode === HOLD_TIME_CATEGORY_CODE;
  return ruleMissing({
    categoryCode,
    triggerCount: rule.triggers?.length ?? 0,
    targetCount: isHoldTime
      ? (rule.holdTimeTargets ?? []).filter((t) => t.enabled).length
      : (rule.targetKeys?.length ?? 0),
    recipientCount: rule.recipientGroups
      ? rule.recipientGroups.filter((g) => g.exists).length
      : (rule.recipients?.trim() ? 1 : 0),
    inApp: !!rule.notifyInApp,
    email: rule.notifyEmail ?? true,
  });
}
